import { useMutation } from "@tanstack/react-query";
import { Alert, Button, Popconfirm } from "@arco-design/web-react";
import { useEffect, useRef, useSyncExternalStore } from "react";
import { useNavigate } from "react-router-dom";

import { isGenericHttpFailureMessage, normalizeHttpError } from "../../../lib/http/errors";
import { copyBudgetVersion } from "../repo/quoteBudgetRepo";
import {
  beginVersionCopyIntent,
  canonicalVersionCopyScope,
  completeVersionCopyIntent,
  type VersionCopyIntent,
} from "../runtime/versionCopyIntent";

const COPY_INTENT_PREFIX = "flower-master:quote-budget:copy";
type CopyWorkflowToken = symbol;
type CopyMutationRequest = {
  attempt: number;
  context: { budgetOrderId: string; generation: number };
  intent: VersionCopyIntent;
  workflowScope: string;
  workflowToken: CopyWorkflowToken;
};

const activeCopyWorkflows = new Map<string, CopyWorkflowToken>();
const copyWorkflowListeners = new Set<() => void>();

/** 订阅跨组件实例共享的普通复制状态。 */
function subscribeCopyWorkflows(listener: () => void) {
  copyWorkflowListeners.add(listener);
  return () => copyWorkflowListeners.delete(listener);
}

/** 通知仍挂载的版本动作刷新来源锁。 */
function notifyCopyWorkflowChange() {
  for (const listener of copyWorkflowListeners) listener();
}

/** 为来源版本获取唯一复制令牌。 */
function acquireCopyWorkflow(sourceBudgetOrderId: string): CopyWorkflowToken | null {
  if (activeCopyWorkflows.has(sourceBudgetOrderId)) return null;
  const token = Symbol(sourceBudgetOrderId);
  activeCopyWorkflows.set(sourceBudgetOrderId, token);
  notifyCopyWorkflowChange();
  return token;
}

/** 仅由当前令牌持有者释放来源锁。 */
function releaseCopyWorkflow(sourceBudgetOrderId: string, token: CopyWorkflowToken) {
  if (activeCopyWorkflows.get(sourceBudgetOrderId) !== token) return;
  activeCopyWorkflows.delete(sourceBudgetOrderId);
  notifyCopyWorkflowChange();
}

/** 复制失败使用可操作中文，避免直接展示 Axios 底层文案。 */
function copyVersionErrorText(error: unknown): string {
  const normalized = normalizeHttpError(error);
  if (normalized.statusCode === 403) {
    return "当前账号没有复制核价版本的权限，请联系管理员。";
  }
  if (normalized.code === "QUOTE_BUDGET_VERSION_CONFLICT") {
    return "核价版本已发生变化，请刷新页面后重试。";
  }
  if (normalized.code === "QUOTE_BUDGET_SCHEMA_NOT_READY") {
    return "核价数据库结构尚未完成升级，请联系管理员完成升级后重试。";
  }
  const message = normalized.message.trim();
  const hasChinese = /[\u3400-\u9fff]/u.test(message);
  const containsTechnicalDetail = /https?:\/\/|request failed|status code|trace|stack|exception|gateway|timeout|sql/i.test(message);
  return message
    && !isGenericHttpFailureMessage(message)
    && hasChinese
    && !containsTechnicalDetail
    && message.length <= 120
    ? `复制版本失败：${message}，请重试`
    : "复制核价版本失败，请稍后重试。";
}

/** 版本动作属性。复制基于已保存版本，版本号由后端返回，前端不计算. */
export type QuoteVersionActionsProps = {
  sourceBudgetOrderId: string;
  /** 当前编辑器请求代次；同 ID 返回时也必须使用新代次。 */
  contextGeneration?: number;
  /** 存在未保存修改时，复制确认文案提示基于已保存版本. */
  isDirty?: boolean;
  /** 快速录入区存在尚未加入明细的文本时，复制确认文案需明确其不会被保存. */
  hasPendingEntryText?: boolean;
  disabled?: boolean;
  canCreate: boolean;
  /** 复制成功且已完成业务确认后的版本导航出口。 */
  onNavigateVersion?: (budgetOrderId: string) => void;
  /** 向编辑器上报复制请求状态，用于冻结当前草稿入口。 */
  onCopyingChange?: (copying: boolean) => void;
};

/** 复制为新版本：先提示确认，成功后跳转到后端返回的新版本编辑页. */
export function QuoteVersionActions({
  sourceBudgetOrderId,
  contextGeneration = 0,
  isDirty,
  hasPendingEntryText = false,
  disabled,
  canCreate,
  onNavigateVersion,
  onCopyingChange,
}: QuoteVersionActionsProps) {
  const navigate = useNavigate();
  const mountedRef = useRef(false);
  const attemptRef = useRef(0);
  const contextRef = useRef({ budgetOrderId: sourceBudgetOrderId, generation: contextGeneration });
  const activeCopyRef = useRef<CopyMutationRequest | null>(null);
  const currentContext = { budgetOrderId: sourceBudgetOrderId, generation: contextGeneration };
  if (
    contextRef.current.budgetOrderId !== currentContext.budgetOrderId
    || contextRef.current.generation !== currentContext.generation
  ) {
    contextRef.current = currentContext;
    activeCopyRef.current = null;
  }
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      activeCopyRef.current = null;
      attemptRef.current += 1;
    };
  }, []);

  const isCurrentRequest = (request: {
    attempt: number;
    context: { budgetOrderId: string; generation: number };
  } | undefined) => Boolean(
    request
    && mountedRef.current
    && activeCopyRef.current?.attempt === request.attempt
    && contextRef.current.budgetOrderId === request.context.budgetOrderId
    && contextRef.current.generation === request.context.generation
  );

  const mutation = useMutation({
    mutationFn: (request: CopyMutationRequest) => copyBudgetVersion(request.context.budgetOrderId, {
      idempotencyKey: request.intent.idempotencyKey,
    }),
    onSuccess: (result, request) => {
      if (!isCurrentRequest(request)) return;
      // 不前端计算版本号：直接用后端返回的新单 ID 跳转.
      if (onNavigateVersion) {
        onNavigateVersion(result.budgetOrderId);
      } else {
        navigate(`/quote/budgets/${result.budgetOrderId}`);
      }
      completeVersionCopyIntent(request.intent);
    },
    onSettled: (_data, _error, request) => {
      releaseCopyWorkflow(request.workflowScope, request.workflowToken);
      if (activeCopyRef.current?.attempt === request.attempt) {
        activeCopyRef.current = null;
      }
    },
  });

  const copy = () => {
    if (disabled) return;
    if (activeCopyRef.current && isCurrentRequest(activeCopyRef.current)) return;
    const workflowScope = canonicalVersionCopyScope(contextRef.current.budgetOrderId);
    const workflowToken = acquireCopyWorkflow(workflowScope);
    if (!workflowToken) return;
    const request = {
      attempt: ++attemptRef.current,
      context: { ...contextRef.current },
      intent: beginVersionCopyIntent(
        `${COPY_INTENT_PREFIX}:${workflowScope}`,
        `copy:${workflowScope}`,
      ),
      workflowScope,
      workflowToken,
    };
    activeCopyRef.current = request;
    mutation.mutate(request);
  };
  const mutationBelongsToCurrentContext = Boolean(
    mutation.variables
    && mutation.variables.context.budgetOrderId === currentContext.budgetOrderId
    && mutation.variables.context.generation === currentContext.generation,
  );
  const copying = useSyncExternalStore(
    subscribeCopyWorkflows,
    () => activeCopyWorkflows.has(canonicalVersionCopyScope(sourceBudgetOrderId)),
    () => false,
  );
  const copyError = mutationBelongsToCurrentContext ? mutation.error : null;
  const onCopyingChangeRef = useRef(onCopyingChange);
  onCopyingChangeRef.current = onCopyingChange;
  useEffect(() => {
    onCopyingChangeRef.current?.(copying);
  }, [copying]);
  useEffect(() => () => {
    onCopyingChangeRef.current?.(false);
  }, []);
  if (!canCreate) return null;
  const button = (
    <Button disabled={disabled || copying} loading={copying}>
      复制为新版本
    </Button>
  );

  return (
    <div className="quote-budget-version-actions">
      <Popconfirm
        title="确认复制为新版本？"
        content={hasPendingEntryText
          ? "快速录入区有未加入内容；继续复制将丢弃这些内容，并基于已保存版本创建新版本。"
          : isDirty
            ? "有未保存修改，复制将基于已保存版本。"
            : "将基于当前已保存版本复制出一个新草稿。"}
        okText="确认复制"
        cancelText="取消"
        okType="primary"
        okButtonProps={{ disabled: disabled || copying }}
        onOk={copy}
        trigger="click"
        unmountOnExit
        autoFocus
        focusLock
      >
        {button}
      </Popconfirm>
      {copyError ? (
        <Alert
          type="error"
          content={copyVersionErrorText(copyError)}
          showIcon
        />
      ) : null}
    </div>
  );
}
